import {
  Crypto,
  Func,
  getLogger,
  hasBranding,
  URL,
  Validators,
} from "@neru/base/utils";

import { trackedRoutes } from "./config";

export interface AdobeDigitalData {
  eventData: Record<string, string>;
  page: {
    content: Record<string, string>;
    pageInfo: Record<string, string>;
  };
  user: {
    profile: {
      profileInfo: Record<string, string>;
    };
  };
}

interface WindowWithSatellite {
  _satellite?: {
    track(identifier: string, details?: any): void;
  };
}

interface WindowWithDigitalData extends Window {
  digitalData: AdobeDigitalData;
}

interface RouteConfig {
  path: string;
  track: "once" | "every";
}

export type RouteWhitelist = Array<RouteConfig>;

const RULE = "allpages";
const CATEGORY = "biz:api:adp:developer";
const INDEX = "products:overview";

const log = getLogger("Adobe Analytics");

const digitalDataTemplate: AdobeDigitalData = {
  eventData: {
    events: "",
    fCTA: "",
    fIndustry: "",
    fLines: "",
    fName: "",
    interactionType: "",
    linesSlider: "",
  },
  page: {
    content: {
      author: "",
      businessSize: "",
      category: "",
      company: "",
      industry: "",
      name: "",
      product: "",
      profRole: "",
      publishDate: "",
      subCategory: "",
      topic: "",
      type: "",
    },
    pageInfo: {
      appVersion: "",
      country: "",
      flowName: "",
      functionDept: "",
      language: "",
      lob: "",
      pageCategory: "",
      pageName: "",
      primaryCategory: CATEGORY,
      siteIdentifier: "",
      spaFlag: "",
      subCategory1: "",
      subCategory2: "",
      subCategory3: "",
      tokenFLag: "",
    },
  },
  user: {
    profile: {
      profileInfo: {
        accountNumber: "",
        accountPlan: "",
        accountStatus: "",
        acctcontractFlag: "",
        acctnumLocations: "",
        country: "",
        email: "",
        loggedinStatus: "",
        responsiveSiteVersion: "",
        userId: "",
        userName: "",
        userType: "",
        visitorType: "",
      },
    },
  },
};

const hasSatellite = (obj: object): obj is WindowWithSatellite =>
  "_satellite" in obj &&
  Validators.isObject<object>(obj._satellite) &&
  "track" in obj._satellite &&
  typeof obj._satellite.track === "function";

const hasDigitalData = (obj: object): obj is WindowWithDigitalData =>
  "digitalData" in obj &&
  Validators.isObject<object>(obj.digitalData) &&
  "page" in obj.digitalData;

const maybeInitDataLayer = (window: object) => {
  if (hasDigitalData(window)) {
    return window.digitalData;
  }

  (window as WindowWithDigitalData).digitalData = digitalDataTemplate;

  return digitalDataTemplate;
};

const updatePageInfo = (pathArray: string[]) => {
  const dataLayer = maybeInitDataLayer(window);

  const lob = CATEGORY.substring(0, CATEGORY.indexOf(":")); // "biz"
  const functionDept = CATEGORY.substring(0, CATEGORY.lastIndexOf(":")); // "biz:api:adp"
  const siteIdentifier = hasBranding(window)
    ? window.branding.vendorId // i.e. "vonage"
    : CATEGORY.substring(CATEGORY.indexOf(":") + 1); // "api:adp:developer"

  dataLayer.page.pageInfo = {
    ...dataLayer.page.pageInfo,
    functionDept,
    lob,
    pageName: `${CATEGORY}:${pathArray.length ? pathArray.join(":") : INDEX}`,
    siteIdentifier,
  };
};

const updateSubCategories = ([path1 = INDEX, path2, path3]: string[]) => {
  const dataLayer = maybeInitDataLayer(window);

  const subCategory1 = `${CATEGORY}:${path1}`;
  const subCategory2 = path2 ? `${subCategory1}:${path2}` : subCategory1;
  const subCategory3 = path3 ? `${subCategory2}:${path3}` : subCategory2;

  dataLayer.page.pageInfo = {
    ...dataLayer.page.pageInfo,
    subCategory1,
    subCategory2,
    subCategory3,
  };
};

const removeUUID = (value: string) => {
  const values = value.split("_");

  if (values.length > 1 && Validators.isUUID(values[0])) {
    return values[1];
  }

  return value;
};

const updateDigitalData = () => {
  const pathArray = window.location.pathname
    .split("/")
    .filter((e) => e)
    .map(removeUUID);

  if (pathArray[0]?.length === 2) {
    pathArray.shift(); // remove locale
  }

  updatePageInfo(pathArray);
  updateSubCategories(pathArray);
};

export const setProfileInfo = async (user?: MFA.UserInfo) => {
  const dataLayer = maybeInitDataLayer(window);

  const info =
    user?.status === "logged-in"
      ? {
          accountNumber: user.accountId,
          email: Crypto.sha256(user.email).toString(),
          loggedinStatus: "logged in",
          userId: String(user.nexmoUserId),
          userName: Crypto.sha256(user.email).toString(),
          visitorType: "customer",
        }
      : {
          accountNumber: "",
          email: "",
          loggedinStatus: "non-logged in",
          userId: "",
          userName: "",
          visitorType: "",
        };

  dataLayer.user.profile.profileInfo = {
    ...dataLayer.user.profile.profileInfo,
    ...info,
  };
};

/**
 * One second debounce is required to prevent falsy tracking of page reloads
 * i.e. authorization related reloads
 */
export const trackPageView = Func.debounce(() => {
  log.info(RULE);
  if (hasSatellite(window)) {
    updateDigitalData();

    log.debug(
      `[${RULE}]: ${
        hasDigitalData(window)
          ? JSON.stringify(window.digitalData, null, 2)
          : "{}"
      }`,
    );

    window._satellite?.track(RULE);
  }
}, 1000);

/**
 * 200ms debounce is required to prevent tracking double button clicks
 */
export const trackEvent = Func.debounce(
  (identifier: string, details?: unknown) => {
    log.info(identifier);
    if (hasSatellite(window)) {
      updateDigitalData();

      log.debug(
        `[${identifier}]: ${
          hasDigitalData(window)
            ? JSON.stringify(window.digitalData, null, 2)
            : "{}"
        }`,
      );

      window._satellite?.track(identifier, details);
    }
  },
  200,
);

export const shouldTrack = (pathname: string) => {
  const matchedRoute = trackedRoutes.find((route) =>
    URL.matchPath(route.path, pathname),
  );

  log.debug(`Should track "${pathname}": ${!!matchedRoute}`);

  if (!matchedRoute) return false;

  if (matchedRoute.track === "once") {
    const trackings = JSON.parse(
      sessionStorage.getItem("trackings") ?? "[]",
    ) as string[];

    const hasBeenTracked = trackings?.includes(pathname);

    log.info(`Has been tracked: ${hasBeenTracked}`);

    if (hasBeenTracked) return false;

    sessionStorage.setItem(
      "trackings",
      JSON.stringify([...trackings, pathname]),
    );

    return true;
  }

  return true;
};
